// AI credits: what each AI read really costs us, in credits a user can see.
//   1 credit = ₹0.01 of our Workers AI cost.
//   Free: 100 credits a day (₹1 of cost) and at most 1,500 a month (₹15 of cost).
//   Bought credits sell at 5× cost, ₹0.05 a credit (CREDIT_PACKS), and never expire. They're used
//   after the free ones; buying is off until CREDITS_STORE is "on" (worker/routes/credits.ts).
// Costs are kept in milli-credits (1/1000 of a credit) so small reads don't round to zero.
import { classifyPromptChars, cleanText, extractPromptChars, fullWindows, quickPassage, type ScanMode } from "./ai";
import { TYPES } from "./catalog";
import { addDays } from "./dates";

/** Workers AI bills $0.011 per 1,000 Neurons beyond the free allowance. */
export const USD_PER_NEURON = 0.011 / 1000;
/** Rupees per dollar used to price credits; AI_USD_INR overrides it. */
export const DEFAULT_USD_INR = 88;
export const PAISE_PER_CREDIT = 1;
export const MILLI = 1000;

export const DEFAULT_DAILY_FREE_CREDITS = 100;
export const DEFAULT_MONTHLY_FREE_CREDITS = 1500;
/** We charge ₹5 for every ₹1 the model costs us. */
export const PRICE_MULTIPLIER = 5;
/** The first credit pack, shown as coming soon. */
export const COMING_SOON_PACK = { credits: 1000, priceInr: (1000 * PAISE_PER_CREDIT * PRICE_MULTIPLIER) / 100 };

export interface CreditPack {
  id: string;
  credits: number;
  priceInr: number;
}

/** Packs on sale (worker/routes/credits.ts): 5× our cost, a little less per credit in bigger packs. */
export const CREDIT_PACKS: readonly CreditPack[] = [
  { id: "pack_1000", ...COMING_SOON_PACK },
  { id: "pack_5000", credits: 5000, priceInr: 225 },
  { id: "pack_20000", credits: 20000, priceInr: 800 },
];
export const packById = (id: string) => CREDIT_PACKS.find((p) => p.id === id) ?? null;

/** Neurons per million tokens, from Cloudflare's pricing page. Add a model here before switching AI_MODEL to it. */
export const MODEL_RATES: Record<string, { input: number; output: number }> = {
  "@cf/meta/llama-3.3-70b-instruct-fp8-fast": { input: 26_668, output: 204_805 },
};
/** A model missing from the table is priced like the most expensive one, so we never undercharge. */
const FALLBACK_RATE = { input: 26_668, output: 204_805 };

export const rateFor = (model: string) => MODEL_RATES[model] ?? FALLBACK_RATE;

/** What a call costs us, in milli-credits, rounded up. */
export function costMilliCredits(model: string, inputTokens: number, outputTokens: number, usdInr = DEFAULT_USD_INR): number {
  const rate = rateFor(model);
  const neurons = (Math.max(0, inputTokens) * rate.input + Math.max(0, outputTokens) * rate.output) / 1_000_000;
  const paise = neurons * USD_PER_NEURON * usdInr * 100;
  return Math.ceil((paise / PAISE_PER_CREDIT) * MILLI);
}

/** About four characters per token for English contracts. */
export const estimateTokens = (chars: number) => Math.ceil(chars / 4);
/** A generous count (three characters per token) for the before-the-call check, so names and numbers can't push a read over budget. */
export const worstCaseTokens = (chars: number) => Math.ceil(chars / 3);
/** The longest answer we let the model write (max_tokens); a full answer is usually under 800. */
export const MAX_OUTPUT_TOKENS = 1500;
/** A typical answer's length, for "about N reads left". */
export const TYPICAL_OUTPUT_TOKENS = 600;

export interface CreditWindow {
  /** Whole credits, rounded up, so a part-used credit shows as used. */
  used: number;
  limit: number;
  remaining: number;
  /** YYYY-MM-DD on which the window starts over, in the workspace's time zone. */
  resetsOn: string;
}

export interface CreditStatus {
  day: CreditWindow;
  month: CreditWindow;
  /** Credits a typical contract read costs, for "about N reads left". */
  typicalRead: number;
  /** Credits that must be left before a read starts: its worst possible cost. */
  reserve: number;
  /** Whole reads the user can still afford today and this month (the smaller of the two). */
  readsLeft: number;
  /** The first pack, and whether credits can be bought on this server (shown as coming soon until then). */
  topUp: { available: boolean; credits: number; priceInr: number };
  /** Credits the user bought and hasn't used. They never expire and are used after the free ones. Missing from older servers. */
  paid?: { balance: number };
  /** Set while the user's limits come from the early-user campaign. */
  earlyUser?: { multiplier: number; endsOn: string | null } | null;
}

export function creditWindow(usedMilli: number, limit: number, resetsOn: string): CreditWindow {
  const used = Math.ceil(usedMilli / MILLI);
  return { used, limit, remaining: Math.max(0, limit - used), resetsOn };
}

/**
 * Reads left: each read needs `reserve` credits free before it starts and then uses about
 * `typicalRead`, so this counts how many times that fits in the smaller allowance.
 */
export function creditStatus(
  day: CreditWindow,
  month: CreditWindow,
  typicalReadMilli: number,
  reserveMilli = typicalReadMilli,
  paid: { balanceMilli: number; storeOpen: boolean } = { balanceMilli: 0, storeOpen: false },
): CreditStatus {
  const typicalRead = Math.max(1, Math.ceil(typicalReadMilli / MILLI));
  const reserve = Math.max(typicalRead, Math.ceil(reserveMilli / MILLI));
  const balance = Math.max(0, Math.floor(paid.balanceMilli / MILLI));
  let left = Math.min(day.remaining, month.remaining) + balance;
  let readsLeft = 0;
  while (left >= reserve && readsLeft < 10_000) {
    left -= typicalRead;
    readsLeft++;
  }
  return { day, month, typicalRead, reserve, readsLeft, topUp: { available: paid.storeOpen, ...COMING_SOON_PACK }, paid: { balance } };
}

/** Which allowance a read of `costMilli` would go over, or null if it fits both. */
export function overLimit(status: Pick<CreditStatus, "day" | "month">, costMilli: number): "day" | "month" | null {
  const need = Math.ceil(costMilli / MILLI);
  if (status.month.remaining < need) return "month";
  if (status.day.remaining < need) return "day";
  return null;
}

// --- what a scan will cost ----------------------------------------------------------------

/** The classify call's answer is tiny. */
const CLASSIFY_OUTPUT_TOKENS = 60;

export interface ScanPlan {
  mode: ScanMode;
  /** Characters of the document after clean-up. */
  totalChars: number;
  /** Characters the scan reads. */
  readChars: number;
  /** A quick scan that leaves parts of the document unread. */
  partial: boolean;
  /** Model calls: one to classify, then one per window. */
  calls: number;
  /** The most the scan can cost (generous token counts, longest answers): checked before it runs. */
  worstMilli: number;
  /** What it usually costs, for "about N credits". */
  typicalMilli: number;
}

// The type isn't known until the first call answers, so plan with the largest type's form.
let largestSchema = 0;
const schemaChars = () => (largestSchema ||= Math.max(...[null, ...TYPES.map((t) => t.id)].map((t) => extractPromptChars(t, ""))));

/** Plans a quick or full scan of `text`: how much it reads, how many calls, and what it can cost. */
export function planScan(text: string, mode: ScanMode, model: string, usdInr = DEFAULT_USD_INR): ScanPlan {
  const clean = cleanText(text);
  const passages = mode === "full" ? fullWindows(clean) : [quickPassage(clean, null).passage];
  const quick = mode === "quick" ? quickPassage(clean, null) : null;
  const classifyChars = classifyPromptChars(clean);
  const cost = (tokens: (chars: number) => number, output: number) =>
    costMilliCredits(model, tokens(classifyChars), CLASSIFY_OUTPUT_TOKENS, usdInr) +
    passages.reduce((sum, p) => sum + costMilliCredits(model, tokens(schemaChars() + p.length), output, usdInr), 0);
  return {
    mode,
    totalChars: clean.length,
    readChars: quick ? quick.readChars : clean.length,
    partial: quick ? quick.partial : false,
    calls: 1 + passages.length,
    worstMilli: cost(worstCaseTokens, MAX_OUTPUT_TOKENS),
    typicalMilli: cost(estimateTokens, TYPICAL_OUTPUT_TOKENS),
  };
}

/**
 * Which allowance a scan would go over. A quick scan must fit today's and this month's credits;
 * a full scan may go past today's but must fit this month's.
 */
export function scanOverLimit(status: Pick<CreditStatus, "day" | "month">, plan: Pick<ScanPlan, "mode" | "worstMilli">): "day" | "month" | null {
  const over = overLimit(status, plan.worstMilli);
  return plan.mode === "full" && over === "day" ? null : over;
}

// --- admin grants ------------------------------------------------------------------------

/** How long a raised limit lasts. "today" ends tonight; "permanent" never ends. */
export const GRANT_DURATIONS = {
  today: 1,
  week: 7,
  month: 30,
  permanent: null,
} as const;
export type GrantDuration = keyof typeof GRANT_DURATIONS;
/** The most an admin can give or set in one go, so a typo can't hand out lakhs of rupees. */
export const MAX_GRANT_CREDITS = 100_000;

export interface CreditGrant {
  id: string;
  kind: "bonus" | "limits";
  /** Bonus credits (kind "bonus"). */
  credits: number | null;
  /** New limits (kind "limits"); null keeps the free one. */
  dailyLimit: number | null;
  monthlyLimit: number | null;
  startsOn: string;
  /** Last day it applies, or null for permanent. */
  endsOn: string | null;
  note: string | null;
  grantedBy: string;
  createdAt: string;
  revokedAt: string | null;
}

/** The last day a limit raised on `today` for `duration` applies, or null if permanent. */
export function grantEndsOn(today: string, duration: GrantDuration): string | null {
  const days = GRANT_DURATIONS[duration];
  return days === null ? null : addDays(today, days - 1);
}

/** Whether a grant still counts on `today`: not revoked, and today is within its days. */
export function isGrantActive(g: Pick<CreditGrant, "startsOn" | "endsOn" | "revokedAt">, today: string): boolean {
  return !g.revokedAt && g.startsOn <= today && (g.endsOn === null || g.endsOn >= today);
}

export interface EffectiveLimits {
  daily: number;
  monthly: number;
  /** Bonus credits included in today's and this month's limits. */
  bonusToday: number;
  bonusMonth: number;
  /** The raised-limits grant in force, if any. */
  override: CreditGrant | null;
}

/**
 * A user's limits on `today`: the newest active "limits" grant replaces the free daily and
 * monthly limits, then bonus credits are added (a day's bonus counts towards its month too,
 * so the monthly limit never blocks credits given for today).
 */
export function effectiveLimits(free: { daily: number; monthly: number }, grants: CreditGrant[], today: string): EffectiveLimits {
  const month = today.slice(0, 7);
  const override =
    grants
      .filter((g) => g.kind === "limits" && isGrantActive(g, today))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))[0] ?? null;
  const bonuses = grants.filter((g) => g.kind === "bonus" && !g.revokedAt);
  const sum = (list: CreditGrant[]) => list.reduce((n, g) => n + (g.credits ?? 0), 0);
  const bonusToday = sum(bonuses.filter((g) => g.startsOn === today));
  const bonusMonth = sum(bonuses.filter((g) => g.startsOn.slice(0, 7) === month && g.startsOn <= today));
  return {
    daily: (override?.dailyLimit ?? free.daily) + bonusToday,
    monthly: (override?.monthlyLimit ?? free.monthly) + bonusMonth,
    bonusToday,
    bonusMonth,
    override,
  };
}

// --- early-user campaign -----------------------------------------------------------------

/**
 * The first 50 users get 10× the free AI credits for a year, as a "limits" grant made at
 * sign-up (worker/ops/campaign.ts). AI_EARLY_USER_SEATS changes the number of seats.
 */
export const EARLY_USERS = {
  grantedBy: "campaign:early-users",
  seats: 50,
  multiplier: 10,
  days: 365,
  note: "Early user: 10× free AI credits for a year",
} as const;

export interface EarlyUserStatus {
  /** Still taking sign-ups. */
  active: boolean;
  seats: number;
  spotsLeft: number;
  multiplier: number;
  days: number;
}

// --- grace credits and falling back to a quick scan ---------------------------------------

/** How far short of a scan's cost (as a share of the limit it goes over) we still let it run, on us. AI_GRACE_PERCENT overrides it. */
export const DEFAULT_AI_GRACE_PERCENT = 5;
/** Grace credits are given as a bonus grant from this "admin", at most once a day per person. */
export const GRACE_GRANTOR = "grace";

/** Credits short of `plan` in each allowance it goes over (0 where it fits), following scanOverLimit's rules. */
export function shortfall(status: Pick<CreditStatus, "day" | "month">, plan: Pick<ScanPlan, "mode" | "worstMilli">): { day: number; month: number } {
  const need = Math.ceil(plan.worstMilli / MILLI);
  return {
    day: plan.mode === "full" ? 0 : Math.max(0, need - status.day.remaining),
    month: Math.max(0, need - status.month.remaining),
  };
}

/** The most grace each allowance allows: `percent`% of its limit, rounded up. */
export const graceAllowance = (status: Pick<CreditStatus, "day" | "month">, percent: number) => ({
  day: Math.ceil((status.day.limit * Math.max(0, percent)) / 100),
  month: Math.ceil((status.month.limit * Math.max(0, percent)) / 100),
});

export interface ScanChoice {
  plan: ScanPlan;
  /** Credits we add as a gift so this scan fits (0 if it fits anyway). */
  grace: number;
  /** Bought credits this scan may need at most, after the free ones. */
  paid: number;
  /** A full scan was asked for but only a quick scan fits. */
  downgraded: boolean;
}

/**
 * Picks the scan to run: the one asked for, then the same with bought credits, then with grace
 * credits, then (for a full scan of a long document) a quick scan the same way. Null if none
 * fits; the caller reports what the requested scan went over.
 */
export function chooseScan(
  status: Pick<CreditStatus, "day" | "month"> & { paid?: { balance: number } },
  plans: { requested: ScanPlan; quick?: ScanPlan | null },
  grace: { percent: number; available: boolean },
): ScanChoice | null {
  const allowance = graceAllowance(status, grace.percent);
  const balance = status.paid?.balance ?? 0;
  const ladder = [plans.requested, ...(plans.requested.mode === "full" && plans.quick?.partial ? [plans.quick] : [])];
  for (const plan of ladder) {
    const downgraded = plan !== plans.requested;
    const short = shortfall(status, plan);
    const need = Math.max(short.day, short.month);
    if (!need) return { plan, grace: 0, paid: 0, downgraded };
    // Bought credits cover what the free ones don't, in either window.
    if (need <= balance) return { plan, grace: 0, paid: need, downgraded };
    const rest = { day: Math.max(0, short.day - balance), month: Math.max(0, short.month - balance) };
    if (grace.available && rest.day <= allowance.day && rest.month <= allowance.month) {
      return { plan, grace: Math.max(rest.day, rest.month), paid: balance, downgraded };
    }
  }
  return null;
}

/**
 * The part of a read's cost taken from bought credits: whatever the free credits left (plus any
 * grace) don't cover, up to the balance. Quick scans must fit today's and this month's free
 * credits; full scans only this month's.
 */
export function paidPart(
  costMilli: number,
  status: Pick<CreditStatus, "day" | "month">,
  mode: ScanMode,
  graceCredits: number,
  balanceMilli: number,
): number {
  const free = (mode === "full" ? status.month.remaining : Math.min(status.day.remaining, status.month.remaining)) + graceCredits;
  return Math.min(Math.max(0, balanceMilli), Math.max(0, costMilli - free * MILLI));
}
